import { useCallback, useSyncExternalStore } from "react";

const LS_KEY = "nomi-crm:workspaceTabs";

/**
 * - `preview`: a record opens in the tab you are on until you keep it
 *   (draft, double-click, Cmd/Ctrl-click). One tab for browsing.
 * - `new-tab`: every other record opens its own tab.
 * - `off`: no tabs; Cmd/Ctrl-click opens a browser tab.
 */
export type WorkspaceTabOpening = "preview" | "new-tab" | "off";

export const WORKSPACE_TAB_LIMITS = [4, 6, 8, 10] as const;

export type WorkspaceTabPreferences = {
  opening: WorkspaceTabOpening;
  maxTabs: number;
};

export const DEFAULT_WORKSPACE_TAB_PREFERENCES: WorkspaceTabPreferences = {
  opening: "preview",
  maxTabs: 6,
};

export const parseWorkspaceTabPreferences = (
  raw: string | null,
): WorkspaceTabPreferences => {
  try {
    const parsed = JSON.parse(
      raw ?? "null",
    ) as Partial<WorkspaceTabPreferences> | null;
    const opening =
      parsed?.opening === "preview" ||
      parsed?.opening === "new-tab" ||
      parsed?.opening === "off"
        ? parsed.opening
        : DEFAULT_WORKSPACE_TAB_PREFERENCES.opening;
    const maxTabs = (WORKSPACE_TAB_LIMITS as readonly number[]).includes(
      Number(parsed?.maxTabs),
    )
      ? Number(parsed?.maxTabs)
      : DEFAULT_WORKSPACE_TAB_PREFERENCES.maxTabs;
    return { opening, maxTabs };
  } catch {
    return DEFAULT_WORKSPACE_TAB_PREFERENCES;
  }
};

let cachedRaw: string | null | undefined;
let memoryRaw: string | null = null;
let cached = DEFAULT_WORKSPACE_TAB_PREFERENCES;

const getSnapshot = (): WorkspaceTabPreferences => {
  let raw = memoryRaw;
  try {
    raw = localStorage.getItem(LS_KEY);
  } catch {
    // Privacy mode: what was chosen on this page load.
  }
  if (raw !== cachedRaw) {
    cachedRaw = raw;
    cached = parseWorkspaceTabPreferences(raw);
  }
  return cached;
};

const listeners = new Set<() => void>();

const subscribe = (callback: () => void) => {
  listeners.add(callback);
  const onStorage = (event: StorageEvent) => {
    if (event.key === LS_KEY) callback();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(callback);
    window.removeEventListener("storage", onStorage);
  };
};

/** Per browser, like the navigation layout. */
export const useWorkspaceTabPreferences = () => {
  const preferences = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const update = useCallback((patch: Partial<WorkspaceTabPreferences>) => {
    memoryRaw = JSON.stringify({ ...getSnapshot(), ...patch });
    try {
      localStorage.setItem(LS_KEY, memoryRaw);
    } catch {
      // Privacy mode: the change lasts until reload.
    }
    listeners.forEach((fn) => fn());
  }, []);
  return [preferences, update] as const;
};
